import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  ShieldCheck,
  Copy,
  ExternalLink,
} from "lucide-react";
import { adminListCreditOrdersAudit } from "@/lib/api/credits.functions";

export const Route = createFileRoute(
  "/_authenticated/portal/admin/credit-orders-audit",
)({
  head: () => ({
    meta: [
      { title: "Credit orders audit — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AuditPage,
});

type IdempotencyState = "ok" | "deduped" | "pending" | "no-itn" | "failed";

const IDEMPOTENCY_META: Record<
  IdempotencyState,
  { label: string; tone: string; Icon: typeof CheckCircle2 }
> = {
  ok: {
    label: "Granted once",
    tone: "bg-emerald-50 text-emerald-800 border-emerald-200",
    Icon: ShieldCheck,
  },
  deduped: {
    label: "Deduped (extra ITNs ignored)",
    tone: "bg-sky-50 text-sky-800 border-sky-200",
    Icon: ShieldCheck,
  },
  pending: {
    label: "Awaiting ITN",
    tone: "bg-amber-50 text-amber-800 border-amber-200",
    Icon: Clock,
  },
  "no-itn": {
    label: "No ITN received",
    tone: "bg-muted text-muted-foreground border-border",
    Icon: AlertTriangle,
  },
  failed: {
    label: "ITN error",
    tone: "bg-rose-50 text-rose-800 border-rose-200",
    Icon: XCircle,
  },
};

function AuditPage() {
  const fetchAudit = useServerFn(adminListCreditOrdersAudit);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin-credit-orders-audit"],
    queryFn: () => fetchAudit(),
    refetchInterval: 15000,
  });

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | IdempotencyState>(
    "all",
  );

  const rows = useMemo(() => {
    const all = (data as any)?.rows ?? [];
    const q = query.trim().toLowerCase();
    return all.filter((r: any) => {
      if (statusFilter !== "all" && r.idempotency !== statusFilter) return false;
      if (!q) return true;
      return (
        r.reference?.toLowerCase().includes(q) ||
        r.paid_provider_ref?.toLowerCase().includes(q) ||
        r.user?.email?.toLowerCase().includes(q) ||
        r.user?.full_name?.toLowerCase().includes(q)
      );
    });
  }, [data, query, statusFilter]);

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading audit…</p>;
  }
  if (error) {
    return (
      <p className="text-sm text-rose-700">
        Could not load audit: {(error as Error).message}
      </p>
    );
  }

  return (
    <section className="space-y-4">
      <header>
        <h2 className="font-display text-xl text-navy">
          Credit orders audit · PayFast ITN events
        </h2>
        <p className="text-xs text-muted-foreground">
          Every credit grant is keyed by <code>reason=pack_purchase</code> +
          order reference. PayFast can post the same ITN multiple times — the
          unique <code>(provider, external_event_id)</code> row plus the
          ledger key make the grant idempotent. Use this page to confirm each
          order was granted exactly once.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search reference, PayFast ref, email…"
          className="h-9 w-64 rounded-md border border-input bg-background px-3 text-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="all">All idempotency states</option>
          <option value="ok">Granted once</option>
          <option value="deduped">Deduped</option>
          <option value="pending">Awaiting ITN</option>
          <option value="no-itn">No ITN</option>
          <option value="failed">ITN error</option>
        </select>
        <button
          type="button"
          onClick={() => refetch()}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm hover:border-navy"
        >
          {isFetching ? "Refreshing…" : "Refresh"}
        </button>
        <span className="ml-auto text-xs text-muted-foreground">
          {rows.length} of {(data as any)?.rows?.length ?? 0} orders
        </span>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No matching orders.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((r: any) => (
            <AuditRow key={r.id} row={r} />
          ))}
        </ul>
      )}
    </section>
  );
}

function AuditRow({ row }: { row: any }) {
  const [open, setOpen] = useState(false);
  const meta = IDEMPOTENCY_META[row.idempotency as IdempotencyState];
  const Icon = meta.Icon;
  return (
    <li className="rounded-xl border border-border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm text-navy">{row.reference}</span>
            <button
              type="button"
              onClick={() => navigator.clipboard?.writeText(row.reference)}
              title="Copy reference"
              className="rounded p-1 text-muted-foreground hover:bg-muted"
            >
              <Copy className="h-3 w-3" />
            </button>
            <Link
              to="/payment-status/$ref"
              params={{ ref: row.reference }}
              className="inline-flex items-center gap-1 text-xs text-navy hover:underline"
            >
              status <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {row.user?.full_name || "—"} · {row.user?.email || "no email"}
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm font-semibold text-navy">
            R{Number(row.amount_zar).toFixed(2)}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {row.credits} credits · {row.pack_code}
          </p>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-[12px] md:grid-cols-4">
        <Cell label="Order status" value={row.status} />
        <Cell
          label="PayFast ref"
          value={row.paid_provider_ref ?? "—"}
          mono
        />
        <Cell
          label="Created"
          value={new Date(row.created_at).toLocaleString("en-ZA")}
        />
        <Cell
          label="Paid at"
          value={
            row.paid_at
              ? new Date(row.paid_at).toLocaleString("en-ZA")
              : "—"
          }
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${meta.tone}`}
        >
          <Icon className="h-3 w-3" /> {meta.label}
        </span>
        <span className="text-[11px] text-muted-foreground">
          ITN events: {row.eventCount}
          {row.completeEventCount > 0
            ? ` (${row.completeEventCount} complete)`
            : ""}
          {row.duplicateCompletes > 0
            ? ` · ${row.duplicateCompletes} duplicate ignored`
            : ""}
          {row.failureCount > 0 ? ` · ${row.failureCount} failed` : ""}
        </span>
        {row.grant ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
            <CheckCircle2 className="h-3 w-3" /> Ledger +{row.grant.delta} on{" "}
            {new Date(row.grant.created_at).toLocaleString("en-ZA")}
          </span>
        ) : (
          <span className="text-[11px] text-amber-700">
            No credit_ledger grant row yet
          </span>
        )}
        {row.events.length > 0 && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="ml-auto rounded-md border border-input bg-background px-2 py-1 text-[11px] hover:border-navy"
          >
            {open ? "Hide" : "Show"} ITN events
          </button>
        )}
      </div>

      {open && row.events.length > 0 && (
        <div className="mt-3 overflow-hidden rounded-md border border-border">
          <table className="w-full text-[11px]">
            <thead className="bg-muted/40 text-left text-muted-foreground">
              <tr>
                <th className="px-2 py-1.5">Received</th>
                <th className="px-2 py-1.5">Event</th>
                <th className="px-2 py-1.5">External ID</th>
                <th className="px-2 py-1.5">Processed</th>
                <th className="px-2 py-1.5">Error</th>
              </tr>
            </thead>
            <tbody>
              {row.events.map((e: any) => (
                <tr key={e.id} className="border-t border-border">
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {new Date(e.created_at).toLocaleString("en-ZA")}
                  </td>
                  <td className="px-2 py-1.5">{e.event_type}</td>
                  <td className="px-2 py-1.5 font-mono">
                    {e.external_event_id}
                  </td>
                  <td className="px-2 py-1.5">
                    {e.processed ? (
                      <span className="text-emerald-700">
                        ✓ {e.processed_at
                          ? new Date(e.processed_at).toLocaleTimeString("en-ZA")
                          : ""}
                      </span>
                    ) : (
                      <span className="text-rose-700">pending / failed</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-rose-700">
                    {e.error || e.error_message || ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </li>
  );
}

function Cell({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="rounded-md bg-muted/30 px-2 py-1.5">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p
        className={`mt-0.5 truncate text-navy ${mono ? "font-mono" : "font-medium"}`}
        title={value}
      >
        {value}
      </p>
    </div>
  );
}
